import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import useSWR, { BareFetcher } from 'swr';

import { DEAL_COMMENT_TYPE, ITEMS_PER_PAGE_ON_AGGREGATE } from './constants';
import { DiscussionFilter, DiscussionSort, TacoRatingFilter } from './types';

function useDiscussionManualPagination({
  dealId,
  type,
  sortBy,
  filterBy,
  tacoRating,
  query,
  paginateOnSearch,
  discussionUrlFn,
  discussionFetcher,
  excludeIds,
  fallbackData,
}: {
  dealId: number;
  type: DEAL_COMMENT_TYPE;
  sortBy: DiscussionSort;
  filterBy: string;
  tacoRating: TacoRatingFilter;
  query: string | undefined;
  paginateOnSearch: boolean;
  discussionUrlFn: Function;
  discussionFetcher: BareFetcher;
  fallbackData: any[];
  excludeIds?: string;
}) {
  const [size, setSize] = useState(1);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [hasMadeInitialRequest, setHasMadeInitialRequest] = useState(
    !fallbackData,
  );

  const { data, mutate, error, isValidating } = useSWR(
    discussionUrlFn({
      dealId,
      type,
      page: size,
      sort: sortBy,
      filter: filterBy as DiscussionFilter,
      tacoRating,
      query,
      paginateOnSearch,
      itemsPerPage: ITEMS_PER_PAGE_ON_AGGREGATE,
      enroll: true,
      excludeIds,
    }),
    discussionFetcher,
    {
      // These options show the cached data (if present) while the new data is being fetched.  This is to reduce
      // the CLS from a skeleton loader.  After the intial request is made, the loader will be shown again
      // when changing the filter or sorting options.
      fallbackData,
      keepPreviousData: false,
      revalidateIfStale: true,
      onSuccess: () => {
        if (!hasMadeInitialRequest) setHasMadeInitialRequest(true);
      },
    },
  );

  const router = useRouter();

  // set initial page if present in query
  useEffect(() => {
    const { page } = router.query;

    if (page) {
      setSize(parseInt(page as string));
    }
  }, [router, setSize]);

  // set page as query param on page change
  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }

    const url = new URL(window.location.href);

    url.searchParams.set('page', size.toString());
    window.history.pushState({}, '', url.toString());
  }, [isFirstLoad, size]);

  return {
    data,
    size,
    setSize,
    error,
    mutate,
    isFinishedPaginating: false,
    isLoadingMore: hasMadeInitialRequest && isValidating && !error,
  };
}

export default useDiscussionManualPagination;
